import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  FiBook,
  FiMic,
  FiFileText,
  FiUsers,
  FiMessageCircle,
  FiHome,
  FiBookOpen,
  FiStar,
  FiVideo,
  FiPlus,
  FiX,
  FiShield,
} from 'react-icons/fi';
import './Sidebar.css';
import liwazaLogoShort from '../assets/liwaza-short.png';
import liwazaLogoWhite from '../assets/liwaza-short-logo-white.png';
import ProfilePicture from '../components/ProfilePicture';
import NotificationInbox from '../components/NotificationInbox';
import { getStoredUser } from '../utils/authSession';
import {
  getPremiumLandingPath,
  isGreOnlySubscriber,
  isIeltsOnlySubscriber,
} from '../utils/subscriptionProductScope';

const Sidebar = () => {
  // eslint-disable-next-line no-unused-vars
  const [user, setUser] = useState(() => getStoredUser());
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1024);
  const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);

  const isCommunityActive = location.pathname.startsWith('/community');
  const [isDarkMode, setIsDarkMode] = useState(true);

  useEffect(() => {
    const handleCommunityDarkModeChange = (event) => {
      setIsDarkMode(event.detail.isDarkMode);
    };

    window.addEventListener(
      'communityDarkModeChange',
      handleCommunityDarkModeChange
    );

    return () => {
      window.removeEventListener(
        'communityDarkModeChange',
        handleCommunityDarkModeChange
      );
    };
  }, []);

  const shouldShowDarkSidebar = isCommunityActive && isDarkMode;

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 1024);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    setIsMoreMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!isMoreMenuOpen) return;

    const onKeyDown = (e) => {
      if (e.key === 'Escape') setIsMoreMenuOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isMoreMenuOpen]);

  useEffect(() => {
    if (!isMobile) return;
    if (!isMoreMenuOpen) return;

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [isMoreMenuOpen, isMobile]);

  // Define menu items based on user premium status
  // Aligned with Home page top navigation icons and order
  const getPremiumMenuItems = () => {
    if (ieltsOnly) {
      return [
        {
          icon: <FiMic size={20} />,
          label: 'English',
          path: '/exams/english/journey',
        },
        {
          icon: <FiMessageCircle size={20} />,
          label: 'Community',
          path: '/community',
        },
      ];
    }
    const items = [
      {
        icon: <FiBook size={20} />,
        label: 'GRE',
        path: '/exams/gre',
      },
      {
        icon: <FiMic size={20} />,
        label: 'English',
        path: '/exams/english/',
      },
      {
        icon: <FiFileText size={20} />,
        label: 'Applications',
        path: '/applications',
      },
      {
        icon: <FiUsers size={20} />,
        label: 'Coaching',
        path: '/coaching',
      },
      {
        icon: <FiMessageCircle size={20} />,
        label: 'Community',
        path: '/community',
      },
      {
        icon: <FiBookOpen size={20} />,
        label: 'Resources',
        path: '/resources',
      },
    ];
    if (greOnly) {
      return items.filter(
        (item) =>
          typeof item.path === 'string' &&
          !item.path.startsWith('/exams/english')
      );
    }
    return items;
  };

  const getFreemiumMenuItems = () => [
    {
      icon: <FiMessageCircle size={20} />,
      label: 'Community',
      path: '/community',
    },
    {
      icon: <FiUsers size={20} />,
      label: 'Coaching',
      path: '/coaching',
    },
    {
      icon: <FiBookOpen size={20} />,
      label: 'Resources',
      path: '/resources',
    },
  ];

  // Coach-specific menu items (coach role)
  const getCoachMenuItems = () => [
    {
      icon: <FiUsers size={20} />,
      label: 'Coaching',
      path: '/coaching',
    },
    {
      icon: <FiMessageCircle size={20} />,
      label: 'Community',
      path: '/community',
    },
    ...(user?.is_document_reviewer
      ? [
          {
            icon: <FiFileText size={20} />,
            label: 'Reviews',
            path: '/coach/document-reviews',
          },
        ]
      : []),
  ];

  // Check if user is a coach
  const isCoach = user?.role?.name === 'coach';
  const isAdmin = user?.role?.name === 'admin';
  const ieltsOnly = Boolean(
    user?.is_premium && isIeltsOnlySubscriber(user)
  );
  const greOnly = Boolean(user?.is_premium && isGreOnlySubscriber(user));
  const adminMenuItem = {
    icon: <FiShield size={20} />,
    label: 'Admin',
    path: '/admin/user-controls',
  };
  const adminWebinarsMenuItem = {
    icon: <FiVideo size={20} />,
    label: 'Webinars admin',
    path: '/admin/webinars',
  };

  const desktopMenuItems = [
    ...(isCoach
      ? getCoachMenuItems()
      : user?.is_premium
        ? getPremiumMenuItems()
        : getFreemiumMenuItems()),
    ...(isAdmin ? [adminWebinarsMenuItem, adminMenuItem] : []),
  ];

  const getPremiumMobileMenuItems = () => {
    if (ieltsOnly) {
      return [
        {
          icon: <FiHome size={24} />,
          label: 'Home',
          path: getPremiumLandingPath(user),
        },
        {
          icon: <FiMic size={24} />,
          label: 'English',
          path: '/exams/english/',
        },
        {
          icon: <FiMessageCircle size={24} />,
          label: 'Community',
          path: '/community',
        },
        {
          icon: <FiPlus size={24} />,
          label: '',
          action: 'more',
        },
      ];
    }
    const row = [
      {
        icon: <FiHome size={24} />,
        label: 'Home',
        path: '/home',
      },
      {
        icon: <FiBook size={24} />,
        label: 'GRE',
        path: '/exams/gre',
      },
      {
        icon: <FiMic size={24} />,
        label: 'English',
        path: '/exams/english/',
      },
      {
        icon: <FiPlus size={24} />,
        label: '',
        action: 'more',
      },
    ];
    if (greOnly) {
      return row.filter(
        (item) =>
          item.action === 'more' ||
          (item.path && !String(item.path).startsWith('/exams/english'))
      );
    }
    return row;
  };

  const getFreemiumMobileMenuItems = () => [
    {
      icon: <FiMessageCircle size={24} />,
      label: 'Community',
      path: '/community',
    },
    {
      icon: <FiUsers size={24} />,
      label: 'Coaching',
      path: '/coaching',
    },
    {
      icon: <FiBookOpen size={24} />,
      label: 'Resources',
      path: '/resources',
    },
  ];

  // Coach-specific mobile menu items
  const getCoachMobileMenuItems = () => [
    {
      icon: <FiUsers size={24} />,
      label: 'Coaching',
      path: '/coaching',
    },
    {
      icon: <FiMessageCircle size={24} />,
      label: 'Community',
      path: '/community',
    },
    ...(user?.is_document_reviewer
      ? [
          {
            icon: <FiFileText size={24} />,
            label: 'Reviews',
            path: '/coach/document-reviews',
          },
        ]
      : []),
  ];

  const mobileMenuItems = [
    ...(isCoach
      ? getCoachMobileMenuItems()
      : user?.is_premium
        ? getPremiumMobileMenuItems()
        : getFreemiumMobileMenuItems()),
    ...(!user?.is_premium && isAdmin
      ? [adminWebinarsMenuItem, adminMenuItem]
      : []),
  ];

  const menuItems = isMobile ? mobileMenuItems : desktopMenuItems;

  const premiumMobileMoreItems = ieltsOnly
    ? [
        ...(isAdmin
          ? [
              {
                icon: <FiVideo size={20} />,
                label: 'Webinars admin',
                path: '/admin/webinars',
              },
              {
                icon: <FiShield size={20} />,
                label: 'Admin',
                path: '/admin/user-controls',
              },
            ]
          : []),
        {
          icon: (
            <ProfilePicture
              user={user}
              alt="Settings"
              context="More menu"
              style={{
                width: '20px',
                height: '20px',
                objectFit: 'cover',
                borderRadius: '50%',
              }}
            />
          ),
          label: 'Settings',
          path: '/settings',
        },
      ]
    : [
        {
          icon: <FiFileText size={20} />,
          label: 'Applications',
          path: '/applications',
        },
        {
          icon: <FiUsers size={20} />,
          label: 'Coaching',
          path: '/coaching',
        },
        {
          icon: <FiMessageCircle size={20} />,
          label: 'Community',
          path: '/community',
        },
        {
          icon: <FiBookOpen size={20} />,
          label: 'Resources',
          path: '/resources',
        },
        ...(isAdmin
          ? [
              {
                icon: <FiVideo size={20} />,
                label: 'Webinars admin',
                path: '/admin/webinars',
              },
              {
                icon: <FiShield size={20} />,
                label: 'Admin',
                path: '/admin/user-controls',
              },
            ]
          : []),
        {
          icon: (
            <ProfilePicture
              user={user}
              alt="Settings"
              context="More menu"
              style={{
                width: '20px',
                height: '20px',
                objectFit: 'cover',
                borderRadius: '50%',
              }}
            />
          ),
          label: 'Settings',
          path: '/settings',
        },
      ];

  return (
    <div
      className={`liwaza-sidebar d-flex flex-column ${shouldShowDarkSidebar ? 'sidebar-dark' : ''}`}
      style={{
        width: '80px',
        minHeight: '100vh',
        backgroundColor: shouldShowDarkSidebar ? '#1D1B20' : '#fefbff',
        borderRight: `1px solid ${shouldShowDarkSidebar ? '#49454F' : '#e0e0e0'}`,
        position: 'fixed',
        top: 0,
        left: 0,
        overflowY: 'auto',
        transition: 'background-color 0.3s ease, border-color 0.3s ease',
      }}
    >
      {/* Logo */}
      <Link
        to={
          isCoach
            ? '/coaching'
            : user?.is_premium
              ? getPremiumLandingPath(user)
              : '/community'
        }
        className={`nav-item ${
          (isCoach
            ? false
            : user?.is_premium
              ? isIeltsOnlySubscriber(user)
                ? location.pathname.startsWith('/exams/english')
                : location.pathname === '/home'
              : location.pathname === '/community')
            ? 'active'
            : ''
        }`}
        style={{
          width: '100%',
          marginTop: '8px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          cursor: 'pointer',
          textDecoration: 'none',
        }}
      >
        <div className="nav-item-content">
          <div className="icon-container">
            <div className="icon-background"></div>
            <div className="icon">
              <img
                src={shouldShowDarkSidebar ? liwazaLogoWhite : liwazaLogoShort}
                alt="Liwaza"
                style={{
                  width: '32px',
                  height: '32px',
                  objectFit: 'contain',
                }}
              />
            </div>
          </div>
          <span className="nav-label">Home</span>
        </div>
      </Link>

      {/* Menu Items */}
      <nav
        className="d-flex flex-column align-items-center"
        style={{ '--tw-space-y-reverse': '0' }}
      >
        {menuItems.map((item, index) => {
          let isActive = false;
          if (item.path) {
            const itemPath =
              typeof item.path === 'object' ? item.path.pathname : item.path;

            // For GRE and English, use startsWith to keep them selected on sub-pages
            if (item.label === 'GRE') {
              isActive = location.pathname.startsWith('/exams/gre');
            } else if (item.label === 'English') {
              isActive = location.pathname.startsWith('/exams/english');
            } else {
              isActive = location.pathname === itemPath;
            }

            if (item.label === 'Applications') {
              isActive =
                location.pathname === '/applications' ||
                location.pathname === itemPath;
            }

            if (
              itemPath === '/coach/document-reviews' ||
              itemPath === '/coach/reviews' ||
              itemPath === '/coach/funding-reviews'
            ) {
              isActive =
                location.pathname === '/coach/document-reviews' ||
                location.pathname === '/coach/reviews' ||
                location.pathname === '/coach/funding-reviews' ||
                location.pathname === '/coaching/document-reviews';
            }

            if (
              item.category &&
              location.pathname === '/exams/gre' &&
              location.state?.activeCategory === item.category
            ) {
              isActive = true;
            } else if (
              item.category &&
              location.pathname === '/exams/gre' &&
              !location.state?.activeCategory
            ) {
              isActive = false;
            }
          }

          if (isMobile && item.action === 'more') {
            return (
              <button
                key={index}
                type="button"
                className={`nav-item nav-item-button ${isMoreMenuOpen ? 'active' : ''}`}
                onClick={() => setIsMoreMenuOpen((v) => !v)}
                aria-label="Open menu"
              >
                <div className="nav-item-content">
                  <div className="icon-container">
                    <div className="icon-background"></div>
                    <div className="icon">{item.icon}</div>
                  </div>
                  <span className="nav-label">{item.label}</span>
                </div>
              </button>
            );
          }

          // On mobile, handle premium action
          if (isMobile && item.action === 'premium') {
            return (
              <div
                key={index}
                className="nav-item premium-mobile-item"
                style={{ cursor: 'pointer' }}
                onClick={() => navigate('/pricing')}
              >
                <div className="nav-item-content">
                  <div className="icon-container">
                    <div
                      className="icon-background"
                      style={{ backgroundColor: 'var(--liwaza-primary)' }}
                    ></div>
                    <div
                      className="icon"
                      style={{ color: 'var(--liwaza-on-primary)' }}
                    >
                      {item.icon}
                    </div>
                  </div>
                  <span
                    className="nav-label"
                    style={{
                      color: 'var(--liwaza-primary)',
                      fontWeight: 'bold',
                    }}
                  >
                    {item.label}
                  </span>
                </div>
              </div>
            );
          }

          // Desktop items and other mobile items remain as links
          return (
            <Link
              key={index}
              to={item.path}
              className={`nav-item ${isActive ? 'active' : ''}`}
            >
              <div className="nav-item-content">
                <div className="icon-container">
                  <div className="icon-background"></div>
                  <div className="icon">{item.icon}</div>
                </div>
                <span className="nav-label">{item.label}</span>
              </div>
            </Link>
          );
        })}
      </nav>

      {/* Mobile "More" menu (premium only) */}
      {isMobile && user?.is_premium && isMoreMenuOpen && (
        <>
          <div
            className="liwaza-more-backdrop"
            onClick={() => setIsMoreMenuOpen(false)}
          />
          <div
            className="liwaza-more-sheet"
            role="dialog"
            aria-modal="true"
            aria-label="More menu"
          >
            <div className="liwaza-more-sheet__panel">
              <div className="liwaza-more-sheet__header">
                <div className="liwaza-more-sheet__handle" />
                <button
                  type="button"
                  className="liwaza-more-sheet__close"
                  onClick={() => setIsMoreMenuOpen(false)}
                  aria-label="Close menu"
                >
                  <FiX size={18} />
                </button>
              </div>

              <div className="liwaza-more-sheet__items">
                <NotificationInbox
                  variant="sheet"
                  onNavigate={() => setIsMoreMenuOpen(false)}
                />
                {premiumMobileMoreItems.map((mi) => (
                  <button
                    key={mi.path}
                    type="button"
                    className="liwaza-more-sheet__item"
                    onClick={() => navigate(mi.path)}
                  >
                    <span className="liwaza-more-sheet__item-icon">
                      {mi.icon}
                    </span>
                    <span className="liwaza-more-sheet__item-label">
                      {mi.label}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Premium Button for non-premium users (desktop only) */}
      {!user?.is_premium && !user?.role.name === 'coach' && !isMobile && (
        <div
          className="d-flex justify-content-center"
          style={{ marginTop: '32px', padding: '0 16px', marginBottom: '16px' }}
        >
          <div
            className="nav-item premium-button"
            style={{ cursor: 'pointer' }}
            onClick={() => navigate('/pricing')}
          >
            <div className="nav-item-content">
              <div className="icon-container">
                <div
                  className="icon-background"
                  style={{
                    backgroundColor: 'var(--liwaza-primary)',
                    borderRadius: '8px',
                  }}
                ></div>
                <div className="icon">
                  <FiStar
                    size={20}
                    style={{ color: 'var(--liwaza-on-primary)' }}
                  />
                </div>
              </div>
              <span
                className="nav-label"
                style={{ color: 'var(--liwaza-primary)', fontWeight: 'bold' }}
              >
                Go premium
              </span>
            </div>
          </div>
        </div>
      )}

      {!isMobile ? (
        <div className="d-flex justify-content-center" style={{ marginBottom: 8 }}>
          <NotificationInbox variant="sidebar" />
        </div>
      ) : null}

      {/* Profile */}
      <div
        className="mt-auto mb-2 d-flex flex-column justify-content-center align-items-center"
        style={{ padding: '0 16px' }}
      >
        <div
          className="nav-item profile-nav-item"
          style={{
            width: '90%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textDecoration: 'none',
            cursor: 'pointer',
          }}
          onClick={() => navigate('/settings')}
        >
          <div className="nav-item-content">
            <div
              className="icon-container profile-icon-container"
              style={{
                width: '40px',
                height: '40px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                borderRadius: '50%',
                transition: 'all 0.3s ease',
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              <div
                className="icon-background profile-icon-background"
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  transition: 'all 0.3s ease',
                  zIndex: 0,
                }}
              />
              <ProfilePicture
                user={user}
                alt="Profile"
                context="Sidebar navigation"
                style={{
                  width: '32px',
                  height: '32px',
                  objectFit: 'cover',
                  borderRadius: '50%',
                  position: 'relative',
                  zIndex: 1,
                }}
              />
            </div>
            <div className="nav-label profile-nav-label">Settings</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
